import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { cn } from '~/src/lib/utils'

export interface Props {
  children: (props: {
    openModal: () => void
    closeModal: () => void
    isOpen: boolean
  }) => React.ReactNode
  trigger?: (props: {
    openModal: () => void
    closeModal: () => void
    isOpen: boolean
  }) => React.ReactNode
  onClose?: () => void
  onOpen?: () => void
  fullScreen?: boolean
  wide?: boolean
  width?: number
  widthClassName?: string
  customClassNames?: string
  show?: boolean
  showCloseButton?: boolean
  blurOverlay?: boolean
  onCloseButtonClick?: () => void
  closeOnSwipeDown?: boolean
  hideOverlay?: boolean
  bottomSheet?: boolean
  disableClickOutsideClose?: boolean
  adjustForSidebar?: boolean
}

const ModalDialog = ({
  wide,
  width,
  widthClassName,
  customClassNames,
  show,
  fullScreen,
  bottomSheet,
  showCloseButton,
  blurOverlay,
  onCloseButtonClick,
  closeOnSwipeDown,
  hideOverlay,
  children,
  trigger,
  onClose,
  onOpen,
  disableClickOutsideClose,
  adjustForSidebar,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const focusRef = React.useRef<HTMLDivElement>(null)

  const handlers = useSwipeable({
    onSwipedDown: () => {
      if (closeOnSwipeDown) {
        closeModal()
      }
    },
  })

  const closeModal = useCallback(() => {
    if (!isOpen) return
    setTimeout(() => {
      setIsOpen(false)
      if (onClose) onClose()
    }, 0)
  }, [isOpen, onClose])

  const openModal = useCallback(() => {
    if (isOpen) return

    setIsOpen(true)
    if (onOpen) onOpen()
  }, [isOpen, onOpen])

  // auto open if `show` is true
  useEffect(() => {
    if (isOpen) return

    const timeout = setTimeout(() => {
      if (show) {
        openModal()
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [show, isOpen, openModal])

  // ensure modal closed at unmount
  useEffect(() => {
    return () => {
      setIsOpen(false)
    }
  }, [])

  return (
    <>
      {!!trigger && trigger({ openModal, closeModal, isOpen })}

      <Transition appear show={isOpen}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => {
            disableClickOutsideClose ? null : closeModal()
          }}
          initialFocus={focusRef}
          static={disableClickOutsideClose}
        >
          {!hideOverlay && (
            <TransitionChild
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div
                className={cn('fixed inset-0 bg-black/50', {
                  'backdrop-blur-sm': blurOverlay,
                })}
              />
            </TransitionChild>
          )}

          <div className='fixed inset-0 flex'>
            <div
              className={clsx('flex w-full justify-center text-center', {
                'flex-1': fullScreen,
                'items-center p-3': !bottomSheet && !fullScreen,
                'items-end': bottomSheet,
              })}
            >
              <TransitionChild
                enter='transition ease-out duration-200'
                enterFrom={clsx('transform opacity-0', {
                  'opacity-0 scale-95': !bottomSheet,
                  'translate-y-full': bottomSheet,
                })}
                enterTo={clsx('transform', {
                  'opacity-100 scale-100': !bottomSheet,
                  'translate-y-0': bottomSheet,
                })}
                leave='transition ease-in duration-100'
                leaveFrom={clsx('transform', {
                  'opacity-100 scale-100': !bottomSheet,
                  'translate-y-0': bottomSheet,
                })}
                leaveTo={clsx('transform', {
                  'opacity-0 scale-95': !bottomSheet,
                  'translate-y-full': bottomSheet,
                })}
              >
                <DialogPanel
                  className={clsx(
                    'dark:bg-darkbg no-scrollbar max-h-[calc(100dvh-50px)] w-full max-w-full transform overflow-auto overscroll-contain bg-white text-left align-middle text-black shadow-2xl transition-all md:max-h-[calc(100dvh-120px)] dark:text-white/90',
                    {
                      'min-h-full': fullScreen,
                      'md:max-w-xl':
                        !width &&
                        !bottomSheet &&
                        !widthClassName &&
                        !fullScreen,
                      'rounded-3xl': !bottomSheet && !fullScreen,
                      'rounded-t-3xl': bottomSheet && !fullScreen,
                      'md:max-w-screen-sm':
                        bottomSheet && !wide && !widthClassName,
                      'md:max-w-screen-lg': wide && !widthClassName,
                      'md:ml-[calc(var(--sidebar-width-collapsed))]':
                        adjustForSidebar,
                    },
                    widthClassName,
                    customClassNames
                  )}
                  {...(bottomSheet ? handlers : {})}
                  style={{
                    width: width || undefined,
                  }}
                >
                  {bottomSheet && closeOnSwipeDown && !showCloseButton && (
                    <div className='pointer-events-none absolute inset-x-0 top-2 flex items-center justify-center'>
                      <div className='h-1 w-16 rounded-full bg-black opacity-35 dark:bg-white' />
                    </div>
                  )}
                  {children({ openModal, closeModal, isOpen })}
                  {showCloseButton && (
                    <button
                      type='button'
                      data-testid='modal-dialog-close'
                      aria-label='Close dialog'
                      className='absolute right-0 top-0 z-10 p-4 opacity-30 outline-none hover:opacity-100 md:right-1 md:top-1 md:p-4'
                      onClick={() => {
                        closeModal()
                        if (onCloseButtonClick) onCloseButtonClick()
                      }}
                    >
                      <XMarkIcon
                        height={24}
                        className='h-[24px] text-current md:h-[30px]'
                      />
                    </button>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default ModalDialog
